'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import FormSelect from 'react-bootstrap/FormSelect';
import Nav from 'react-bootstrap/Nav';
import Table from 'react-bootstrap/Table';

import type { IDatasetStatisticsHistory, IPropFillTable, IStatisticsHistoryColumn, IStatisticsHistoryTable } from '@/lib/statistics';

import styles from '@/styles/DatasetStatistics.module.scss';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDay(timestamp: string): string {
  const month = MONTHS[parseInt(timestamp.slice(5, 7), 10) - 1];
  return `${month} ${parseInt(timestamp.slice(8, 10), 10)}`;
}

function formatRate(rate: number): string {
  const pct = rate * 100;
  return pct >= 99.95 ? '100%' : `${pct.toFixed(1)}%`;
}

function movementClass(value: number, previous: number | null): string | undefined {
  if (previous === null) {
    return undefined;
  }
  if (value === previous) {
    return styles.steady;
  }
  return value > previous ? styles.up : styles.down;
}

// Rows to render: runs where something in this table changed against the
// previous run, plus the newest run (the current state) and the oldest
// (the baseline). On slow-moving datasets this collapses long stretches
// of identical runs.
function changedRunIndices(size: number, differs: (index: number) => boolean): Array<number> {
  const kept: Array<number> = [];
  for (let i = 0; i < size; i++) {
    if (i === 0 || i === size - 1 || differs(i)) {
      kept.push(i);
    }
  }
  return kept;
}

// The run label in the first column of every table row.
function VersionCell({ column }: { column: IStatisticsHistoryColumn }) {
  return (
    <th scope="row" className={styles.labelCell} title={`${column.version} (UTC)`}>
      <span className={styles.versionDate}>{formatDay(column.timestamp)}</span>
      <span className={styles.versionTime}>{column.timestamp.slice(11, 16)}</span>
    </th>
  );
}

function CountCell({ value, previous, isTotal }: { value: number | null, previous: number | null, isTotal?: boolean }) {
  const cellClass = classNames('numeric', { [styles.totalCell]: isTotal });
  if (value === null) {
    return <td className={classNames(cellClass, styles.missing)}>–</td>;
  }
  let title = undefined;
  if (previous !== null && value !== previous) {
    const delta = value - previous;
    const signed = `${delta > 0 ? '+' : '−'}${NUMBER_FORMAT.format(Math.abs(delta))}`;
    const pct = previous > 0 ? ` (${delta > 0 ? '+' : '−'}${(Math.abs(delta) / previous * 100).toFixed(1)}%)` : '';
    title = `${signed}${pct} vs previous run`;
  }
  return (
    <td className={classNames(cellClass, movementClass(value, previous))} title={title}>
      {NUMBER_FORMAT.format(value)}
    </td>
  );
}

type FillCellProps = {
  count: number | null
  total: number | null
  previousCount: number | null
  previousTotal: number | null
}

function FillCell({ count, total, previousCount, previousTotal }: FillCellProps) {
  if (count === null || total === null || total === 0) {
    return <td className={classNames('numeric', styles.missing)}>–</td>;
  }
  const rate = count / total;
  const previousRate = previousCount !== null && previousTotal !== null && previousTotal > 0
    ? previousCount / previousTotal : null;
  let title = `${NUMBER_FORMAT.format(count)} of ${NUMBER_FORMAT.format(total)} entities`;
  if (previousRate !== null && previousRate !== rate) {
    const deltaPts = (rate - previousRate) * 100;
    title += ` · ${deltaPts > 0 ? '+' : '−'}${Math.abs(deltaPts).toFixed(2)}pt vs previous run`;
  }
  return (
    <td className={classNames('numeric', movementClass(rate, previousRate))} title={title}>
      {formatRate(rate)}
    </td>
  );
}

type CountHistoryTableProps = {
  columns: Array<IStatisticsHistoryColumn>
  table: IStatisticsHistoryTable
  totalLabel: string
}

function CountHistoryTable({ columns, table, totalLabel }: CountHistoryTableProps) {
  const kept = changedRunIndices(columns.length, (i) =>
    table.totals[i] !== table.totals[i + 1] ||
    table.rows.some((row) => row.values[i] !== row.values[i + 1]));
  return (
    <div className={styles.tableWrapper}>
      <Table size="sm" className={styles.historyTable}>
        <thead>
          <tr>
            <th className={styles.labelCell}>Run</th>
            {table.rows.map((row) => (
              <th key={row.key} className="numeric"><code title={row.label}>{row.key}</code></th>
            ))}
            <th className={classNames('numeric', styles.totalCell)}>{totalLabel}</th>
          </tr>
        </thead>
        <tbody>
          {kept.map((i) => (
            <tr key={columns[i].version}>
              <VersionCell column={columns[i]} />
              {table.rows.map((row) => (
                <CountCell key={row.key} value={row.values[i]} previous={row.values[i + 1] ?? null} />
              ))}
              <CountCell value={table.totals[i]} previous={table.totals[i + 1] ?? null} isTotal />
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function PropFillTable({ columns, table }: { columns: Array<IStatisticsHistoryColumn>, table: IPropFillTable }) {
  const kept = changedRunIndices(columns.length, (i) =>
    table.totals[i] !== table.totals[i + 1] ||
    table.rows.some((row) => row.counts[i] !== row.counts[i + 1]));
  return (
    <div className={styles.tableWrapper}>
      <Table size="sm" className={classNames(styles.historyTable, styles.fillTable)}>
        <thead>
          <tr>
            <th className={styles.labelCell}>Run</th>
            {table.rows.map((row) => (
              <th key={row.property} className="numeric" title={row.property}><code>{row.property}</code></th>
            ))}
            <th className={classNames('numeric', styles.totalCell)}>Entities</th>
          </tr>
        </thead>
        <tbody>
          {kept.map((i) => (
            <tr key={columns[i].version}>
              <VersionCell column={columns[i]} />
              {table.rows.map((row) => (
                <FillCell
                  key={row.property}
                  count={row.counts[i]}
                  total={table.totals[i]}
                  previousCount={row.counts[i + 1] ?? null}
                  previousTotal={table.totals[i + 1] ?? null}
                />
              ))}
              <CountCell value={table.totals[i]} previous={table.totals[i + 1] ?? null} isTotal />
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

type TabKey = 'things' | 'targets' | 'countries' | 'fill';

// The tabs are addressable via fragment identifier (e.g. /statistics/#countries),
// so other pages can deep-link a specific view without making the route dynamic.
const TAB_FRAGMENTS: Record<TabKey, string> = {
  things: 'types',
  targets: 'targets',
  countries: 'countries',
  fill: 'fill',
};

export function DatasetStatisticsView({ history }: { history: IDatasetStatisticsHistory }) {
  const hasTargets = history.targets.rows.length > 0;
  const hasCountries = history.countries.rows.length > 0;
  const hasFill = history.propFill.length > 0;
  const [tab, setTab] = useState<TabKey>('things');
  const [schema, setSchema] = useState<string>(history.propFill[0]?.schema);
  const fillTable = history.propFill.find((t) => t.schema === schema) ?? history.propFill[0];

  useEffect(() => {
    const fragment = window.location.hash.replace(/^#/, '');
    const entry = Object.entries(TAB_FRAGMENTS).find(([, f]) => f === fragment);
    if (entry === undefined) {
      return;
    }
    const key = entry[0] as TabKey;
    if ((key === 'targets' && !hasTargets) || (key === 'countries' && !hasCountries) || (key === 'fill' && !hasFill)) {
      return;
    }
    // Syncing from location.hash, which only exists client-side after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTab(key);
  }, [hasTargets, hasCountries, hasFill]);

  const selectTab = (key: TabKey) => {
    setTab(key);
    window.history.replaceState(null, '', `#${TAB_FRAGMENTS[key]}`);
  };

  return (
    <div>
      <Nav variant="tabs" activeKey={tab} onSelect={(key) => key && selectTab(key as TabKey)} className={styles.tabs}>
        <Nav.Item>
          <Nav.Link eventKey="things">Entities by type</Nav.Link>
        </Nav.Item>
        {hasTargets && (
          <Nav.Item>
            <Nav.Link eventKey="targets">Targets</Nav.Link>
          </Nav.Item>
        )}
        {hasCountries && (
          <Nav.Item>
            <Nav.Link eventKey="countries">Countries</Nav.Link>
          </Nav.Item>
        )}
        {hasFill && (
          <Nav.Item>
            <Nav.Link eventKey="fill">Property fill</Nav.Link>
          </Nav.Item>
        )}
      </Nav>
      {tab === 'things' && (
        <CountHistoryTable columns={history.columns} table={history.things} totalLabel="Total" />
      )}
      {tab === 'targets' && (
        <CountHistoryTable columns={history.columns} table={history.targets} totalLabel="Total" />
      )}
      {tab === 'countries' && (
        <CountHistoryTable columns={history.columns} table={history.countries} totalLabel="All entities" />
      )}
      {tab === 'fill' && fillTable && (
        <>
          <FormSelect
            className={styles.schemaPicker}
            value={fillTable.schema}
            onChange={(e) => setSchema(e.target.value)}
            aria-label="Entity type"
          >
            {history.propFill.map((t) => (
              <option key={t.schema} value={t.schema}>{t.schema}</option>
            ))}
          </FormSelect>
          <PropFillTable columns={history.columns} table={fillTable} />
        </>
      )}
    </div>
  );
}
